import { confirmDialog, reportLoadError, notify } from '../utils/notify';
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { KeyRound, Plus, Save, ShieldCheck, Trash2, TriangleAlert, UserCheck, UserX } from 'lucide-react';
import './AdminPanel.css';
import { settingsAPI, userAPI, permissionsAPI } from '../services/api';
import api from '../services/api';

const AdminPanel = () => {
  const { user, refreshPermissions } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);
  
  // Settings
  const [settings, setSettings] = useState({});
  const [groupedSettings, setGroupedSettings] = useState({});
  const [settingsChanged, setSettingsChanged] = useState(false);
  
  // Permissions (por rol)
  const [permissions, setPermissions] = useState({});
  const [permissionsChanged, setPermissionsChanged] = useState(false);
  const [selectedRole, setSelectedRole] = useState('hr_manager');
  
  // Activity Logs
  const [logs, setLogs] = useState([]);
  const [logsStats, setLogsStats] = useState(null);
  const [logsFilters, setLogsFilters] = useState({
    start_date: '',
    end_date: '',
    user_id: '',
    action_type: '',
    limit: 100
  });
  
  // Create User Modal
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    // Guards get the employee role; security_guard is no longer offered here.
    role: 'employee'
  });
  
  // System Stats
  const [systemStats, setSystemStats] = useState(null);
  
  // Users
  const [users, setUsers] = useState([]);
  
  // Estados para edición de usuario (password, delete, permisos individuales)
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Estados para permisos individuales
  const [showUserPermissionsModal, setShowUserPermissionsModal] = useState(false);
  const [editingUserPermissions, setEditingUserPermissions] = useState({});
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserName, setEditingUserName] = useState('');

  // Recursos disponibles para permisos
  const ALL_RESOURCES = [
    'dashboard', 'employees', 'attendance', 'leaves', 'payroll', 
    'finance', 'benefits', 'contracts', 'documents', 'reports', 'settings'
  ];

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      notify(t('Access Denied: Super Admin only'), 'error');
      return;
    }

    // Carga inicial según tab activo
    if (activeTab === 'settings') loadSettings();
    else if (activeTab === 'logs') loadLogs();
    else if (activeTab === 'stats') loadSystemStats();
    else if (activeTab === 'users') loadUsers();
    else if (activeTab === 'permissions') loadPermissions();
  }, [activeTab, user?.role]);

  // Cargar permisos cuando cambia el rol seleccionado
  useEffect(() => {
    if (activeTab === 'permissions' && selectedRole) {
      loadPermissions();
    }
  }, [activeTab, selectedRole]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      const settingsObj = {};
      response.data.settings.forEach(setting => {
        settingsObj[setting.setting_key] = setting.setting_value;
      });
      setSettings(settingsObj);
      setGroupedSettings(response.data.grouped);
      setSettingsChanged(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      reportLoadError(error, t('Error loading settings'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      reportLoadError(error, t('Error loading users'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getLogs(logsFilters);
      setLogs(response.data.logs || []);
      setLogsStats(response.data.stats);
    } catch (error) {
      console.error('Error loading logs:', error);
      reportLoadError(error, t('Error loading activity logs'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const loadSystemStats = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getStatistics();
      setSystemStats(response.data.statistics);
    } catch (error) {
      console.error('Error loading stats:', error);
      reportLoadError(error, t('Error loading system statistics'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionsAPI.getRolePermissions(selectedRole);
      
      const permsByResource = {};
      
      // ⭐ El backend devuelve directamente can_view, can_create, can_edit, etc.
      if (Array.isArray(response.data.permissions)) {
        response.data.permissions.forEach(perm => {
          permsByResource[perm.resource] = {
            can_view: perm.can_view || false,
            can_create: perm.can_create || false,
            can_edit: perm.can_edit || false,
            can_delete: perm.can_delete || false,
            can_approve: perm.can_approve || false
          };
        });
      }
      
      // Agregar recursos faltantes con valores false
      ALL_RESOURCES.forEach(resource => {
        if (!permsByResource[resource]) {
          permsByResource[resource] = {
            can_view: false,
            can_create: false,
            can_edit: false,
            can_delete: false,
            can_approve: false
          };
        }
      });
      
      setPermissions({[selectedRole]: Object.entries(permsByResource).map(([resource, perms]) => ({
        resource,
        ...perms
      }))});
      
      setPermissionsChanged(false);
    } catch (error) {
      console.error('❌ Error loading permissions:', error);
      notify(t('Error loading permissions: {message}', { message: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSettingsChanged(true);
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      // bulkUpdate takes { key: value } (it builds the list itself).
      const response = await settingsAPI.bulkUpdate(settings);
      const skipped = response.data.skipped || [];
      if (skipped.length > 0) {
        notify(t('Saved, except settings that do not exist: {keys}', { keys: skipped.join(', ') }), 'warning');
      } else {
        notify(t('Settings saved successfully!'), 'success');
      }
      setSettingsChanged(false);
      loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      notify(error.response?.data?.message || t('Error saving settings'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      await userAPI.create(newUserForm);
      notify(t('User created successfully!'), 'success');
      setShowCreateUserModal(false);
      setNewUserForm({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'employee'
      });
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      notify(error.response?.data?.message || t('Error creating user'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (role, resource, permType, value) => {
    setPermissions(prev => ({
      ...prev,
      [role]: prev[role].map(perm => 
        perm.resource === resource 
          ? { ...perm, [permType]: value }
          : perm
      )
    }));
    setPermissionsChanged(true);
  };

  const handleSavePermissions = async () => {
    try {
      setLoading(true);
      
      // ⭐ Construir payload compatible con tu esquema (can_view, can_create, etc.)
      const permissionsToSave = [];
      
      Object.keys(permissions).forEach(role => {
        permissions[role].forEach(perm => {
          permissionsToSave.push({
            role,
            resource: perm.resource,
            can_view: perm.can_view || false,
            can_create: perm.can_create || false,
            can_edit: perm.can_edit || false,
            can_delete: perm.can_delete || false,
            can_approve: perm.can_approve || false
          });
        });
      });
      
      await permissionsAPI.bulkUpdate(permissionsToSave);
      
      // Si el rol modificado es el mismo que el del usuario actual,
      // refrescar sus permisos para que se apliquen inmediatamente
      if (selectedRole === user.role) {
        const refreshResult = await refreshPermissions();
        if (refreshResult.success) {
          notify(t('Permissions saved and applied successfully!'), 'success');
        } else {
          notify(t('Permissions saved, but failed to refresh current session. Please log out and log in again to see changes.'), 'warning');
        }
      } else {
        notify(t('Permissions saved successfully! Users with this role will see changes on next login.'), 'success');
      }
      
      setPermissionsChanged(false);
      loadPermissions();
    } catch (error) {
      console.error('Error saving permissions:', error);
      notify(t('Error saving permissions: {message}', { message: error.response?.data?.message || error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== FUNCIONES PARA PERMISOS INDIVIDUALES ==========
  const openUserPermissions = (userItem) => {
    setEditingUserId(userItem.id);
    setEditingUserName(`${userItem.first_name} ${userItem.last_name}`);
    setEditingUserPermissions({});
    loadUserSpecificPermissions(userItem.id);
    setShowUserPermissionsModal(true);
  };

  const loadUserSpecificPermissions = async (userId) => {
    try {
      const res = await permissionsAPI.getUserPermissions(userId);
      const perms = {};
      
      // ⭐ El backend devuelve directamente can_view, can_create, can_edit, etc.
      if (Array.isArray(res.data.permissions)) {
        res.data.permissions.forEach(p => {
          if (!perms[p.resource]) {
            perms[p.resource] = {};
          }
          // Mapear las columnas directamente
          perms[p.resource].view = p.can_view || false;
          perms[p.resource].create = p.can_create || false;
          perms[p.resource].update = p.can_edit || false;  // ⭐ can_edit → update (para el frontend)
          perms[p.resource].delete = p.can_delete || false;
          perms[p.resource].approve = p.can_approve || false;
        });
      }
      
      setEditingUserPermissions(perms);
    } catch (err) {
      console.error('❌ Error cargando permisos individuales:', err);
      notify(t('Error loading the user permissions: {message}', { message: err.response?.data?.message || err.message }), 'error');
    }
  };

  const handleIndividualPermissionChange = (resource, action, value) => {
    setEditingUserPermissions(prev => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: value
      }
    }));
  };

  const handleSaveUserPermissions = async () => {
    try {
      const permissionsToSave = {};
      
      Object.entries(editingUserPermissions).forEach(([resource, actions]) => {
        permissionsToSave[resource] = {
          can_view: actions.view || false,
          can_create: actions.create || false,
          can_edit: actions.update || false,
          can_delete: actions.delete || false,
          can_approve: actions.approve || false
        };
      });
      
      const payload = { permissions: permissionsToSave };
      await permissionsAPI.updateUserPermissions(editingUserId, payload);
      
      notify(t('User permissions updated!'), 'success');
      loadUsers();
      
      if (editingUserId === user.id) {
        await refreshPermissions();
        notify(t('Your own permissions were updated. The changes apply immediately.'), 'info');
      }
      
      setShowUserPermissionsModal(false);
      setEditingUserId(null);
      setEditingUserName('');
      setEditingUserPermissions({});
    } catch (err) {
      notify(t('Error saving permissions: {message}', { message: err.response?.data?.message || err.message }), 'error');
    }
  };
  // ========== FUNCIONES PARA GESTIÓN DE USUARIOS ==========
  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    if (!await confirmDialog(newStatus === 'active' ? t('Activate this user?') : t('Deactivate this user?'))) return;
    try {
      await api.put(`/users/${userId}`, { status: newStatus });
      notify(newStatus === 'active' ? t('User activated!') : t('User deactivated!'), 'success');
      loadUsers();
    } catch (error) {
      notify(t('Error updating the status: {message}', { message: error.response?.data?.message || error.message }), 'error');
    }
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
  };

  const changePassword = async () => {
    if (!newPassword || !confirmPassword) return notify(t('Fill in both password fields'), 'warning');
    if (newPassword !== confirmPassword) return notify(t('The passwords do not match'), 'warning');
    if (newPassword.length < 8) return notify(t('The password must be at least 8 characters'), 'warning');
    try {
      await userAPI.resetPassword(selectedUser.id, { new_password: newPassword });
      notify(t('Password changed!'), 'success');
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      notify(t('Error changing the password: {message}', { message: error.response?.data?.message || error.message }), 'error');
    }
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/users/${selectedUser.id}`);
      notify(t('User deleted!'), 'success');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      notify(t('Error deleting the user: {message}', { message: error.response?.data?.message || error.message }), 'error');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}`, { role: newRole });
      notify(t('User role updated!'), 'success');
      loadUsers();
    } catch (error) {
      notify(t('Error updating the role: {message}', { message: error.response?.data?.message || error.message }), 'error');
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="adminpanel-page">
        <div className="empty adm-denied">
          <h1 className="page-title">{t('Access Denied')}</h1>
          <p>{t('This area is restricted to Super Administrators only.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="adminpanel-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Super Admin Panel')}</h1>
          <p className="page-sub">{t('System configuration and management')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabbar">
        <button className={`tabbar__tab${activeTab === 'settings' ? ' is-active' : ''}`} onClick={() => setActiveTab('settings')}>
          {t('Settings')}
        </button>
        <button className={`tabbar__tab${activeTab === 'users' ? ' is-active' : ''}`} onClick={() => setActiveTab('users')}>
          {t('Users')}
        </button>
        <button className={`tabbar__tab${activeTab === 'permissions' ? ' is-active' : ''}`} onClick={() => setActiveTab('permissions')}>
          {t('Permissions (Roles)')}
        </button>
        <button className={`tabbar__tab${activeTab === 'logs' ? ' is-active' : ''}`} onClick={() => setActiveTab('logs')}>
          {t('Activity Logs')}
        </button>
        <button className={`tabbar__tab${activeTab === 'stats' ? ' is-active' : ''}`} onClick={() => setActiveTab('stats')}>
          {t('Statistics')}
        </button>
      </div>

      {/* Banners de cambios */}
      {settingsChanged && activeTab === 'settings' && (
        <div className="adm-banner">
          <span className="adm-banner__text"><TriangleAlert aria-hidden="true" /> {t('You have unsaved changes')}</span>
          <button onClick={handleSaveSettings} className="btn btn--primary adm-save"><Save aria-hidden="true" /> {t('Save All Changes')}</button>
        </div>
      )}

      {permissionsChanged && activeTab === 'permissions' && (
        <div className="adm-banner">
          <span className="adm-banner__text"><TriangleAlert aria-hidden="true" /> {t('You have unsaved permission changes')}</span>
          <button onClick={handleSavePermissions} className="btn btn--primary adm-save"><Save aria-hidden="true" /> {t('Save Permissions')}</button>
        </div>
      )}

      {/* Contenido principal */}
      <div className="adm-content">
        {loading ? (
          <div className="empty">{t('Loading...')}</div>
        ) : (
          <>
            {activeTab === 'settings' && (
              <div className="adm-tab">
                <h2 className="adm-title">{t('System Settings')}</h2>
                {Object.entries(groupedSettings).map(([category, categorySettings]) => (
                  <div key={category} className="card adm-group">
                    <h3 className="adm-group__title">{t(category)}</h3>
                    <div className="adm-settings">
                      {categorySettings.map(setting => (
                        <div key={setting.setting_key} className="adm-setting">
                          <div className="adm-setting__info">
                            <label className="adm-setting__label">{t(setting.setting_key.replace(/_/g, ' '))}</label>
                            <p className="adm-setting__desc">{t(setting.description || 'No description')}</p>
                          </div>
                          <div className="adm-setting__control">
                            {setting.setting_type === 'boolean' ? (
                              <label className="adm-switch">
                                <input
                                  type="checkbox"
                                  checked={settings[setting.setting_key] === 'true'}
                                  onChange={(e) => handleSettingChange(setting.setting_key, e.target.checked.toString())}
                                />
                                <span className="adm-switch__slider"></span>
                              </label>
                            ) : (
                              <input
                                type="text"
                                value={settings[setting.setting_key] || ''}
                                onChange={(e) => handleSettingChange(setting.setting_key, e.target.value)}
                                className="input adm-setting-input"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="adm-tab">
                <div className="adm-head">
                  <h2 className="adm-title">{t('User Management')}</h2>
                  <button onClick={() => setShowCreateUserModal(true)} className="btn btn--primary adm-create-user">
                    <Plus aria-hidden="true" /> {t('Create User')}
                  </button>
                </div>

                <div className="adm-table-wrap">
                  <table className="data-table adm-users">
                    <thead>
                      <tr>
                        <th>{t('Email')}</th>
                        <th>{t('Name')}</th>
                        <th>{t('Role')}</th>
                        <th>{t('Status')}</th>
                        <th>{t('Created')}</th>
                        <th>{t('Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="empty">
                            {t('No users found')}
                          </td>
                        </tr>
                      ) : (
                        users.map(userItem => (
                          <tr key={userItem.id}>
                            <td><strong>{userItem.email}</strong></td>
                            <td>{userItem.first_name} {userItem.last_name}</td>
                            <td>
                              <select
                                value={userItem.role || 'employee'}
                                onChange={(e) => updateUserRole(userItem.id, e.target.value)}
                                className="select adm-role-select"
                              >
                                <option value="super_admin">{t('Super Admin')}</option>
                                <option value="hr_manager">{t('HR Manager')}</option>
                                <option value="finance_manager">{t('Finance Manager')}</option>
                                <option value="operations_manager">{t('Operations Manager')}</option>
                                <option value="employee">{t('Employee')}</option>
                              </select>
                            </td>
                            <td>
                              <StatusBadge status={userItem.status || 'active'}>
                                {t(userItem.status || 'active')}
                              </StatusBadge>
                            </td>
                            <td>{new Date(userItem.created_at).toLocaleDateString(locale)}</td>
                            <td className="adm-actions-cell">
                              <div className="adm-actions">
                                <button
                                  className="btn btn--secondary btn--icon"
                                  onClick={() => toggleUserStatus(userItem.id, userItem.status || 'active')}
                                  title={userItem.status === 'active' ? t('Deactivate user') : t('Activate user')}
                                  aria-label={userItem.status === 'active' ? t('Deactivate user') : t('Activate user')}
                                >
                                  {userItem.status === 'active' ? <UserX aria-hidden="true" /> : <UserCheck aria-hidden="true" />}
                                </button>
                                
                                <button
                                  className="btn btn--secondary btn--icon"
                                  onClick={() => openPasswordModal(userItem)}
                                  title={t('Change password')}
                                  aria-label={t('Change password')}
                                >
                                  <KeyRound aria-hidden="true" />
                                </button>
                                
                                <button
                                  className="btn btn--secondary btn--icon btn--danger-text"
                                  onClick={() => openDeleteModal(userItem)}
                                  title={t('Delete user')}
                                  aria-label={t('Delete user')}
                                >
                                  <Trash2 aria-hidden="true" />
                                </button>

                                {/* Botón para editar permisos individuales */}
                                <button
                                  className="btn btn--secondary adm-perms-btn"
                                  onClick={() => openUserPermissions(userItem)}
                                  title={t('Edit individual permissions')}
                                >
                                  <ShieldCheck aria-hidden="true" /> {t('Permissions')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'permissions' && (
              <div className="adm-tab">
                <h2 className="adm-title">{t('Role Permissions')}</h2>
                
                <div className="adm-role-picker">
                  <label className="field-label">{t('Select Role')}:</label>
                  <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="select">
                    <option value="hr_manager">{t('HR Manager')}</option>
                    <option value="finance_manager">{t('Finance Manager')}</option>
                    <option value="operations_manager">{t('Operations Manager')}</option>
                    <option value="employee">{t('Employee')}</option>
                                <option value="security_guard">{t('Security Guard')}</option>
                  </select>
                </div>

                {loading ? (
                  <div className="empty">
                    <p>{t('Loading permissions for {role}...', { role: selectedRole })}</p>
                  </div>
                ) : !permissions[selectedRole] || permissions[selectedRole].length === 0 ? (
                  <div className="empty">
                    <p>{t('No permissions found for {role}', { role: selectedRole })}</p>
                  </div>
                ) : (
                  <div className="adm-perms">
                    <div className="adm-table-wrap">
                      <table className="data-table adm-perms-table">
                        <thead>
                          <tr>
                            <th>{t('Resource')}</th>
                            <th>{t('View')}</th>
                            <th>{t('Create')}</th>
                            <th>{t('Edit')}</th>
                            <th>{t('Delete')}</th>
                            <th>{t('Approve')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {permissions[selectedRole]?.map((perm, index) => (
                            <tr key={index}>
                              <td className="adm-resource">{t(perm.resource)}</td>
                              <td>
                                <label className="adm-switch">
                                  <input
                                    type="checkbox"
                                    checked={perm.can_view || false}
                                    onChange={(e) => handlePermissionChange(selectedRole, perm.resource, 'can_view', e.target.checked)}
                                  />
                                  <span className="adm-switch__slider"></span>
                                </label>
                              </td>
                              <td>
                                <label className="adm-switch">
                                  <input
                                    type="checkbox"
                                    checked={perm.can_create || false}
                                    onChange={(e) => handlePermissionChange(selectedRole, perm.resource, 'can_create', e.target.checked)}
                                  />
                                  <span className="adm-switch__slider"></span>
                                </label>
                              </td>
                              <td>
                                <label className="adm-switch">
                                  <input
                                    type="checkbox"
                                    checked={perm.can_edit || false}
                                    onChange={(e) => handlePermissionChange(selectedRole, perm.resource, 'can_edit', e.target.checked)}
                                  />
                                  <span className="adm-switch__slider"></span>
                                </label>
                              </td>
                              <td>
                                <label className="adm-switch">
                                  <input
                                    type="checkbox"
                                    checked={perm.can_delete || false}
                                    onChange={(e) => handlePermissionChange(selectedRole, perm.resource, 'can_delete', e.target.checked)}
                                  />
                                  <span className="adm-switch__slider"></span>
                                </label>
                              </td>
                              <td>
                                <label className="adm-switch">
                                  <input
                                    type="checkbox"
                                    checked={perm.can_approve || false}
                                    onChange={(e) => handlePermissionChange(selectedRole, perm.resource, 'can_approve', e.target.checked)}
                                  />
                                  <span className="adm-switch__slider"></span>
                                </label>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'logs' && (
              <div className="adm-tab">
                <h2 className="adm-title">{t('Activity Logs')}</h2>
                {logsStats && (
                  <div className="kpi-grid adm-kpis">
                    <div className="kpi">
                      <span className="kpi__label">{t('Total Logs')}</span>
                      <span className="kpi__value">{logsStats.total_logs || 0}</span>
                    </div>
                    <div className="kpi">
                      <span className="kpi__label">{t('Today')}</span>
                      <span className="kpi__value">{logsStats.today_logs || 0}</span>
                    </div>
                    <div className="kpi">
                      <span className="kpi__label">{t('This Week')}</span>
                      <span className="kpi__value">{logsStats.week_logs || 0}</span>
                    </div>
                  </div>
                )}
                <div className="adm-table-wrap">
                  <table className="data-table adm-logs">
                    <thead>
                      <tr>
                        <th>{t('Timestamp')}</th>
                        <th>{t('User')}</th>
                        <th>{t('Action')}</th>
                        <th>{t('Resource')}</th>
                        <th>{t('Details')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="empty">
                            {t('No activity logs found')}
                          </td>
                        </tr>
                      ) : (
                        logs.map((log, index) => (
                          <tr key={index}>
                            <td>{new Date(log.timestamp).toLocaleString(locale)}</td>
                            <td>{log.user_email}</td>
                            <td>
                              <span className="status status--neutral adm-action">
                                {t(log.action_type)}
                              </span>
                            </td>
                            <td>{t(log.resource_type)}</td>
                            <td className="adm-details">{log.details}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="adm-tab">
                <h2 className="adm-title">{t('System Statistics')}</h2>
                {!systemStats ? (
                  <div className="empty">
                    <p>{t('Loading statistics...')}</p>
                  </div>
                ) : (
                  <>
                    <div className="adm-stats">
                      <h3 className="adm-group__title">{t('Users & Authentication')}</h3>
                      <div className="kpi-grid">
                        <div className="kpi">
                          <h3 className="kpi__label">{t('Total Users')}</h3>
                          <p className="kpi__value">{systemStats?.users?.total_users || 0}</p>
                        </div>
                        <div className="kpi">
                          <h3 className="kpi__label">{t('Active Users')}</h3>
                          <p className="kpi__value">{systemStats?.users?.active_users || 0}</p>
                        </div>
                        <div className="kpi">
                          <h3 className="kpi__label">{t('Unique Roles')}</h3>
                          <p className="kpi__value">{systemStats?.users?.unique_roles || 0}</p>
                        </div>
                      </div>
                    </div>

                    {/* ... resto de las secciones de stats iguales */}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modales */}
      {showCreateUserModal && (
        <Modal open title={t('Create New User')} onClose={() => setShowCreateUserModal(false)} size="md">
            <div className="adm-dialog">
              <form onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}>
                <div className="adm-field">
                  <label className="field-label">{t('Email')} *</label>
                  <input className="input"
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="adm-field">
                  <label className="field-label">{t('Password')} *</label>
                  <input className="input"
                    type="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                    required
                  />
                </div>
                <div className="adm-field">
                  <label className="field-label">{t('First Name')}</label>
                  <input className="input"
                    type="text"
                    value={newUserForm.first_name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, first_name: e.target.value })}
                  />
                </div>
                <div className="adm-field">
                  <label className="field-label">{t('Last Name')}</label>
                  <input className="input"
                    type="text"
                    value={newUserForm.last_name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, last_name: e.target.value })}
                  />
                </div>
                <div className="adm-field">
                  <label className="field-label">{t('Role')}</label>
                  <select className="select"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  >
                    <option value="employee">{t('Employee')}</option>
                    <option value="hr_manager">{t('HR Manager')}</option>
                    <option value="finance_manager">{t('Finance Manager')}</option>
                    <option value="operations_manager">{t('Operations Manager')}</option>
                    <option value="super_admin">{t('Super Admin')}</option>
                  </select>
                </div>
                <div className="adm-form-actions">
                  <button type="button" className="btn btn--secondary" onClick={() => setShowCreateUserModal(false)}>
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary">
                    {t('Create User')}
                  </button>
                </div>
              </form>
            </div>
        </Modal>
      )}

      {showPasswordModal && (
        <Modal open title={t('Change Password')} onClose={() => setShowPasswordModal(false)} size="md">
            <div className="adm-dialog">
              <div className="adm-field">
                <label className="field-label">{t('New Password')}</label>
                <input className="input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="adm-field">
                <label className="field-label">{t('Confirm Password')}</label>
                <input className="input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <div className="adm-form-actions">
                <button className="btn btn--secondary" onClick={() => setShowPasswordModal(false)}>
                  {t('Cancel')}
                </button>
                <button className="btn btn--primary" onClick={changePassword}>
                  {t('Change Password')}
                </button>
              </div>
            </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal open title={t('Delete User')} onClose={() => setShowDeleteModal(false)} size="md">
            <div className="adm-dialog">
              <p>{t('Are you sure you want to delete {name}?', { name: `${selectedUser?.first_name} ${selectedUser?.last_name}` })}</p>
              <p className="adm-danger-note">{t('This action cannot be undone.')}</p>
              <div className="adm-form-actions">
                <button className="btn btn--secondary" onClick={() => setShowDeleteModal(false)}>
                  {t('Cancel')}
                </button>
                <button className="btn btn--danger" onClick={deleteUser}>
                  {t('Delete User')}
                </button>
              </div>
            </div>
        </Modal>
      )}

      {/* Modal de permisos individuales */}
      {showUserPermissionsModal && (
        <Modal
          open
          title={t('Individual Permissions')}
          subtitle={t('{name} (overrides the role)', { name: editingUserName })}
          onClose={() => setShowUserPermissionsModal(false)}
          size="lg"
          footer={(
            <>
              <button className="btn btn--secondary" onClick={() => setShowUserPermissionsModal(false)}>
                {t('Cancel')}
              </button>
              <button className="btn btn--primary" onClick={handleSaveUserPermissions}>
                {t('Save Permissions')}
              </button>
            </>
          )}
        >
            <div className="adm-dialog">
              <div className="adm-perms-grid">
                {ALL_RESOURCES.map(resource => (
                  <div key={resource} className="adm-perm">
                    <h4>{t(resource.charAt(0).toUpperCase() + resource.slice(1))}</h4>
                    <div className="adm-perm__row">
                      <label className="adm-check">
                        <input
                          type="checkbox"
                          checked={editingUserPermissions[resource]?.view || false}
                          onChange={(e) => handleIndividualPermissionChange(resource, 'view', e.target.checked)}
                        />
                        {t('View')}
                      </label>
                      <label className="adm-check">
                        <input
                          type="checkbox"
                          checked={editingUserPermissions[resource]?.create || false}
                          onChange={(e) => handleIndividualPermissionChange(resource, 'create', e.target.checked)}
                        />
                        {t('Create')}
                      </label>
                      <label className="adm-check">
                        <input
                          type="checkbox"
                          checked={editingUserPermissions[resource]?.update || false}
                          onChange={(e) => handleIndividualPermissionChange(resource, 'update', e.target.checked)}
                        />
                        {t('Edit')}
                      </label>
                      <label className="adm-check">
                        <input
                          type="checkbox"
                          checked={editingUserPermissions[resource]?.delete || false}
                          onChange={(e) => handleIndividualPermissionChange(resource, 'delete', e.target.checked)}
                        />
                        {t('Delete')}
                      </label>
                      <label className="adm-check">
                        <input
                          type="checkbox"
                          checked={editingUserPermissions[resource]?.approve || false}
                          onChange={(e) => handleIndividualPermissionChange(resource, 'approve', e.target.checked)}
                        />
                        {t('Approve')}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminPanel;
